import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useInRouterContext, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isLicenseRequired } from '../../lib/licenseStatus';
import { useOnboardingOptional } from '../onboarding/OnboardingContext';
import { useOptInPromptOptional } from '../optInPrompts/OptInPromptContext';
import {
  fetchFeedbackPrompt,
  markFeedbackPromptShown,
  updateFeedbackPromptOptOut,
  type FeedbackSource,
} from '../../services/appFeedbackService';

/**
 * App feedback dialog state for the whole signed-in app.
 *
 *  - `open()` — the sidebar "Feedback" entry; always available.
 *  - `requestAutoStart()` — called by FeedbackAutoStart in AppLayout on every page change.
 *    The first call after sign-in asks the API whether the prompt is due (every
 *    10th login, not opted out). A due prompt never covers the first page after
 *    sign-in: it waits for the next page the user opens (a later call), then for
 *    the onboarding tour and a due opt-in prompt to be out of the way (never two
 *    modals at once), and the API records that it was shown for this login
 *    (usability audit §5.3). Calls after that do nothing.
 *  - `setOptOut()` — "Nicht mehr automatisch anzeigen" in the dialog (optimistic).
 *  - Never auto-opens while the licence notice is shown or in an automated
 *    browser (end-to-end suites must not be blocked by a modal).
 */
export interface FeedbackContextValue {
  isOpen: boolean;
  source: FeedbackSource;
  open: () => void;
  close: () => void;
  /** The stored "don't show automatically" choice; null until loaded. */
  optOut: boolean | null;
  setOptOut: (value: boolean) => Promise<void>;
  optOutSaveFailed: boolean;
  requestAutoStart: () => void;
}

export const FeedbackContext = createContext<FeedbackContextValue | null>(null);

function isAutomatedBrowser(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch {
    return false;
  }
}

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const isAuthenticated = auth.state?.isAuthenticated === true;
  const onboarding = useOnboardingOptional();
  const onboardingBusy = onboarding !== null && (!onboarding.autoStartSettled || onboarding.isOpen);
  // A due opt-in prompt (e.g. the Instagram campaign) goes first; never two modals at once.
  const optInBusy = useOptInPromptOptional()?.busy === true;
  const [isOpen, setIsOpen] = useState(false);
  const [source, setSource] = useState<FeedbackSource>('menu');
  const [optOut, setOptOutState] = useState<boolean | null>(null);
  const [optOutSaveFailed, setOptOutSaveFailed] = useState(false);
  const [promptPending, setPromptPending] = useState(false);
  // True once the user has moved past the first page after sign-in.
  const [movedOn, setMovedOn] = useState(false);
  const attemptedRef = useRef(false);
  const requestSeq = useRef(0);
  const promptSeq = useRef(0);

  // A sign-out ends the session: close the dialog and forget what was loaded.
  useEffect(() => {
    if (isAuthenticated) return;
    attemptedRef.current = false;
    requestSeq.current += 1;
    promptSeq.current += 1;
    setIsOpen(false);
    setPromptPending(false);
    setMovedOn(false);
    setOptOutState(null);
    setOptOutSaveFailed(false);
  }, [isAuthenticated]);

  const loadOptOut = useCallback(() => {
    const seq = ++requestSeq.current;
    void fetchFeedbackPrompt()
      .then((state) => {
        if (seq === requestSeq.current) setOptOutState(state.optOut);
      })
      .catch(() => undefined);
  }, []);

  const requestAutoStart = useCallback(() => {
    if (!isAuthenticated) return;
    if (attemptedRef.current) {
      // A later call: the user opened another page.
      setMovedOn(true);
      return;
    }
    if (isAutomatedBrowser() || isLicenseRequired()) return;
    attemptedRef.current = true;
    const seq = ++promptSeq.current;
    void fetchFeedbackPrompt()
      .then((state) => {
        if (seq !== promptSeq.current) return;
        setOptOutState((current) => current ?? state.optOut);
        if (state.show) setPromptPending(true);
      })
      .catch(() => undefined);
  }, [isAuthenticated]);

  // The due prompt waits until the onboarding tour's automatic start is decided and the tour is closed,
  // and until a due opt-in prompt was answered or closed.
  useEffect(() => {
    if (!promptPending || !movedOn || onboardingBusy || optInBusy || isOpen) return;
    setPromptPending(false);
    setSource('prompt');
    setOptOutSaveFailed(false);
    setIsOpen(true);
    void markFeedbackPromptShown().catch(() => undefined);
  }, [promptPending, movedOn, onboardingBusy, optInBusy, isOpen]);

  const open = useCallback(() => {
    setSource('menu');
    setOptOutSaveFailed(false);
    setIsOpen(true);
    if (optOut === null) loadOptOut();
  }, [optOut, loadOptOut]);

  const close = useCallback(() => setIsOpen(false), []);

  const setOptOut = useCallback(async (value: boolean): Promise<void> => {
    const previous = optOut;
    const seq = ++requestSeq.current;
    setOptOutState(value);
    setOptOutSaveFailed(false);
    try {
      const saved = await updateFeedbackPromptOptOut(value);
      if (seq === requestSeq.current) setOptOutState(saved);
    } catch {
      if (seq === requestSeq.current) {
        setOptOutState(previous);
        setOptOutSaveFailed(true);
      }
    }
  }, [optOut]);

  const value = useMemo<FeedbackContextValue>(
    () => ({ isOpen, source, open, close, optOut, setOptOut, optOutSaveFailed, requestAutoStart }),
    [isOpen, source, open, close, optOut, setOptOut, optOutSaveFailed, requestAutoStart],
  );

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
}

/** The feedback state, or null outside a FeedbackProvider (e.g. isolated page tests). */
export function useFeedbackOptional(): FeedbackContextValue | null {
  return useContext(FeedbackContext);
}

/**
 * Rendered by AppLayout: landing in the signed-in app shell is the signal that
 * the feedback prompt may open automatically. No-op without a provider.
 */
export function FeedbackAutoStart() {
  return useInRouterContext() ? <RoutedAutoStart /> : <AutoStartOnce pathname="" />;
}

/** Every page change calls again — the signal that the user moved past the first page. */
function RoutedAutoStart() {
  return <AutoStartOnce pathname={useLocation().pathname} />;
}

function AutoStartOnce({ pathname }: { pathname: string }) {
  const requestAutoStart = useFeedbackOptional()?.requestAutoStart;
  useEffect(() => {
    requestAutoStart?.();
  }, [requestAutoStart, pathname]);
  return null;
}
